import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useAccountGateBypass } from '@/hooks/useAccountGateBypass'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'

interface ProtectedRouteProps {
  children: React.ReactNode
  roles?: string[]
}

export function ProtectedRoute({ children, roles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth()
  const { shouldBypassAccountGates } = useAccountGateBypass()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/welcome" state={{ from: location }} replace />
  }

  // #signup-revamp Phase 2F: route non-active users to the state-aware
  // /onboarding-status page. Legacy pending_verification users with no
  // onboarding submission keep the old wizard redirect for backwards compat;
  // every other non-active status (pending_email/_review/_match/_contract,
  // rejected) goes to /onboarding-status which renders the right sub-view.
  //
  // #1344 — both status redirects are skipped while an admin is
  // impersonating: the onboarding / verification / contract layer is the
  // account owner's to complete, and bouncing the admin onto it makes a
  // pending account impossible to inspect. Auth + role checks still apply.
  if (!shouldBypassAccountGates && user && user.status === 'pending_verification') {
    const onboardingSubmitted = sessionStorage.getItem('hoteliate_onboarding_submitted')
    if (!onboardingSubmitted) {
      // User registered but didn't complete onboarding - send back to wizard
      return <Navigate to="/onboarding" replace />
    }
    return <Navigate to="/pending-approval" replace />
  }
  if (!shouldBypassAccountGates && user && user.status && user.status !== 'active') {
    return <Navigate to="/onboarding-status" replace />
  }

  // Check role-based access if roles are specified
  if (roles && user) {
    const hasRequiredRole = roles.some(role =>
      user.userType === role || user.roles?.includes(role)
    )
    if (!hasRequiredRole) {
      return <Navigate to="/dashboard" replace />
    }
  }

  return <>{children}</>
}